import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CompletedBookingResponse, clubsBookingApi } from '@/api/clubs-booking';
import { removePublicCartItemForEvent } from '@/utils/publicCart';
import PublicClubHeader from '@/components/public/PublicClubHeader';
import ParentPortalShell from '@/components/parent/ParentPortalShell';
import { auth } from '@/store/authStore';
import { formatCurrencyFromCents } from '@/utils/currency';

const PublicBookingCompletedPage = () => {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [result, setResult] = useState<CompletedBookingResponse | null>(null);

  const totalDisplay = useMemo(() => {
    if (!result) return '';
    return formatCurrencyFromCents(result.booking.totalPriceCents, result.booking.currency);
  }, [result]);
  const preDiscountTotalDisplay = useMemo(() => {
    if (!result) return '';
    const value = result.booking.preDiscountTotalCents ?? result.booking.totalPriceCents;
    return formatCurrencyFromCents(value, result.booking.currency);
  }, [result]);
  const discountTotalDisplay = useMemo(() => {
    if (!result) return '';
    return formatCurrencyFromCents(result.booking.discountTotalCents || 0, result.booking.currency);
  }, [result]);
  const bookingReference = useMemo(() => {
    if (!result) return '';
    if (result.booking.paymentReference?.trim()) return result.booking.paymentReference.trim();
    return `BK-${result.booking._id.slice(-8).toUpperCase()}`;
  }, [result]);

  const getChildIndexesForCartItem = (item: { childIndexes?: number[] }, childCount: number) => {
    const validIndexes = (item.childIndexes || [])
      .map((childIndex) => Number(childIndex))
      .filter((childIndex) => Number.isInteger(childIndex) && childIndex >= 0 && childIndex < childCount);
    return validIndexes.length ? Array.from(new Set(validIndexes)) : Array.from({ length: childCount }, (_, index) => index);
  };

  const formatSessionSummary = useCallback((sessionId: string) => {
    const session = (result?.event.sessions || []).find((entry) => entry.id === sessionId);
    if (!session) return sessionId;
    return `${new Date(session.startDateTime).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ${new Date(session.startDateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  }, [result?.event.sessions]);

  const bookingCartItems = useMemo(() => {
    if (!result) return [];
    if (result.booking.cartItems?.length) return result.booking.cartItems;
    return [{
      ticketTypeId: result.booking.ticketTypeId || '',
      sessionIds: result.booking.sessionIds || [],
      childIndexes: [],
      addOnSelections: result.booking.addOnSelections || [],
    }];
  }, [result]);

  const ticketBreakdown = useMemo(() => {
    if (!result) return [];
    const childCount = Math.max(1, result.booking.children?.length || result.booking.quantity || 1);
    return bookingCartItems.flatMap((item, itemIndex) => {
      const ticket = (result.event.ticketTypes || []).find((entry) => entry.id === item.ticketTypeId);
      const childIndexes = getChildIndexesForCartItem(item, childCount);
      const attendeeCount = childIndexes.length;
      const attendeeLabel = `${attendeeCount} attendee${attendeeCount === 1 ? '' : 's'}`;
      const sessionIds = Array.from(new Set((item.sessionIds || result.booking.sessionIds || []).map(String).filter(Boolean)));
      const ticketName = ticket?.name || 'Ticket';
      const ticketPriceCents = Number(ticket?.priceCents ?? result.booking.unitPriceCents ?? 0);

      if (ticket?.mode === 'specific_sessions' || !ticket) {
        return sessionIds.map((sessionId) => ({
          key: `${itemIndex}-${item.ticketTypeId || 'ticket'}-${sessionId}`,
          title: ticketName,
          subtitle: formatSessionSummary(sessionId),
          detail: `${attendeeLabel} x ${formatCurrencyFromCents(ticketPriceCents, result.booking.currency)}`,
          amountCents: ticketPriceCents * attendeeCount,
        }));
      }

      return [{
        key: `${itemIndex}-${item.ticketTypeId || 'ticket'}-all`,
        title: ticketName,
        subtitle: `${Math.max(1, sessionIds.length)} session${sessionIds.length === 1 ? '' : 's'} included`,
        detail: `${attendeeLabel} x ${formatCurrencyFromCents(ticketPriceCents, result.booking.currency)}`,
        amountCents: ticketPriceCents * attendeeCount,
      }];
    });
  }, [result, bookingCartItems, formatSessionSummary]);

  const addOnBreakdown = useMemo(() => {
    if (!result) return [];
    return bookingCartItems.flatMap((item, itemIndex) => (item.addOnSelections || []).flatMap((selection) => {
      const addOn = (result.event.addOns || []).find((entry) => entry.id === selection.addOnId);
      const addOnName = addOn?.name || 'Add-on';
      const addOnPriceCents = Number(addOn?.priceCents || 0);
      const childCount = Math.max(1, result.booking.children?.length || result.booking.quantity || 1);
      const attendeeCount = getChildIndexesForCartItem(selection, childCount).length;
      return Array.from(new Set((selection.sessionIds || []).map(String).filter(Boolean))).map((sessionId) => ({
        key: `${itemIndex}-${selection.addOnId}-${sessionId}`,
        title: addOnName,
        subtitle: formatSessionSummary(sessionId),
        detail: `${attendeeCount} attendee${attendeeCount === 1 ? '' : 's'} x ${formatCurrencyFromCents(addOnPriceCents, result.booking.currency)}`,
        amountCents: addOnPriceCents * attendeeCount,
      }));
    }));
  }, [result, bookingCartItems, formatSessionSummary]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const bookingId = searchParams.get('booking_id');
    if (!sessionId && !bookingId) {
      setError('Missing booking reference.');
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await clubsBookingApi.confirmCompletedPublicBooking(sessionId || undefined, bookingId || undefined);
        setResult(data);
        if (data.event?.id) {
          removePublicCartItemForEvent(data.event.id);
        } else if (eventSlug) {
          removePublicCartItemForEvent(eventSlug);
        }
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not confirm booking');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [searchParams, eventSlug]);

  if (loading) {
    return <main className="min-h-screen bg-[#f3f4f8] p-6">Confirming booking...</main>;
  }

  if (error || !result) {
    return <main className="min-h-screen bg-[#f3f4f8] p-6 text-red-700">{error || 'Booking not found.'}</main>;
  }

  const isFreeBooking = result.booking.totalPriceCents === 0;
  const isCashBalanceDue = result.booking.paymentMethod === 'cash' && !isFreeBooking;
  const isParentLoggedIn = (() => {
    const user = auth.getUser();
    return !!(user.isAuthenticated && user.role === 'parent');
  })();

  const content = (
      <div className="w-full px-3 pb-8 pt-6 sm:px-4 md:px-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
        <h1 className="text-3xl font-semibold text-slate-900">Booking completed</h1>
        <p className="mt-2 text-slate-600">
          {isFreeBooking
            ? 'Your booking is confirmed. No payment is due.'
            : isCashBalanceDue
            ? 'Your booking is confirmed. Please pay cash on the day.'
            : 'Your payment was received and your booking is confirmed.'}
        </p>
        <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Booking reference</p>
          <p className="mt-1 text-lg font-semibold text-emerald-900">{bookingReference}</p>
        </div>
        {result.parentEmailNoticeType === 'activation' && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            An email has been sent that will allow you to create a password for your account and manage your booking.
          </div>
        )}
        {result.parentEmailNoticeType === 'existing_account' && (
          <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
            We sent an email to your existing parent account with a link to view and manage this booking.
          </div>
        )}

        <div className="mt-6 grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
          <div><span className="font-medium">Activity:</span> {result.event.title}</div>
          <div><span className="font-medium">Date:</span> {new Date(result.event.startDate).toLocaleString('en-GB')}</div>
          <div><span className="font-medium">Location:</span> {result.event.location}</div>
          <div><span className="font-medium">Parent:</span> {result.booking.parentName} ({result.booking.parentEmail})</div>
          <div>
            <span className="font-medium">Attendees:</span>{' '}
            {(result.booking.children || [])
              .map((child) => [child.firstName, child.lastName].filter(Boolean).join(' ').trim())
              .filter(Boolean)
              .join(', ') || 'Child'}
          </div>

          <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-white p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Booking breakdown</p>
            {ticketBreakdown.map((line) => (
              <div key={line.key} className="flex items-start justify-between gap-3 border-t border-slate-100 pt-2 first:border-t-0 first:pt-0">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800">{line.title}</p>
                  <p className="text-xs text-slate-500">{line.subtitle}</p>
                  <p className="text-xs text-slate-600">{line.detail}</p>
                </div>
                <p className="shrink-0 font-medium text-slate-800">
                  {formatCurrencyFromCents(line.amountCents, result.booking.currency)}
                </p>
              </div>
            ))}
            {!!addOnBreakdown.length && (
              <div className="space-y-2 border-t border-slate-200 pt-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Add-ons</p>
                {addOnBreakdown.map((line) => (
                  <div key={line.key} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800">{line.title}</p>
                      <p className="text-xs text-slate-500">{line.subtitle}</p>
                      <p className="text-xs text-slate-600">{line.detail}</p>
                    </div>
                    <p className="shrink-0 font-medium text-slate-800">
                      {formatCurrencyFromCents(line.amountCents, result.booking.currency)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div><span className="font-medium">Subtotal:</span> {preDiscountTotalDisplay}</div>
          <div><span className="font-medium">Discounts:</span> -{discountTotalDisplay}</div>
          <div>
            <span className="font-medium">
              {isFreeBooking ? 'Total due:' : (isCashBalanceDue ? 'Total to pay on the day:' : 'Total paid:')}
            </span>{' '}
            {totalDisplay}
          </div>
          <div><span className="font-medium">Payment status:</span> {result.booking.paymentStatus}</div>
          {!!result.booking.appliedDiscounts?.length && (
            <div>
              <span className="font-medium">Applied discount details:</span>{' '}
              {result.booking.appliedDiscounts.map((discount) => `${discount.summary} (-${formatCurrencyFromCents(discount.amountCents, result.booking.currency)})`).join(' | ')}
            </div>
          )}
        </div>

        <div className="mt-6">
          <Link
            to={eventSlug ? `/book/${eventSlug}` : '/'}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm hover:bg-slate-50"
          >
            Back to activity page
          </Link>
        </div>
        </div>
      </div>
  );

  if (isParentLoggedIn) {
    return (
      <ParentPortalShell title="Booking complete" subtitle={result.event.title} showHeaderCard={false}>
        {content}
      </ParentPortalShell>
    );
  }

  return (
    <main className="min-h-screen bg-[#f3f4f8]">
      <PublicClubHeader
        clubName="Session Monkey"
        preferSessionMonkeyFallbackLogo
        cartHref="/cart"
      />
      {content}
    </main>
  );
};

export default PublicBookingCompletedPage;

